import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bookKey, getBook, listLanguages, updateBook } from "../api";
import { useT } from "../i18n";
import LanguagePicker from "./LanguagePicker";

/**
 * BookLanguage is what language a book is written in.
 *
 * It matters a long way from here. Every brief sent to a language model is
 * written in English and names the language the answer must come back in — the
 * cast, each character's description, a picture's brief. A Korean book recorded
 * as Persian had its characters described in Persian.
 *
 * A book starts with no language and takes one from the script of its first
 * chapter, so this is a correction rather than a step: the row says what was
 * detected, and changing it is a search and a tap. "Not chosen" stays on the list because
 * a book whose chapters are not all one language is better off saying nothing
 * than saying the wrong thing — the briefs then go by the text in front of them.
 */
export default function BookLanguage({ bookID }: { bookID: string }) {
  const t = useT();
  const client = useQueryClient();
  const book = useQuery({
    queryKey: bookKey(bookID, { draft: true }),
    queryFn: () => getBook(bookID, { draft: true }),
  });
  const languages = useQuery({
    queryKey: ["languages"],
    queryFn: listLanguages,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const choose = useMutation({
    mutationFn: (language: string) => updateBook(bookID, { language }),
    onSuccess: () => {
      // The cast, the pictures and the preparation panel all read the book.
      void client.invalidateQueries({ queryKey: ["book", bookID] });
      void client.invalidateQueries({ queryKey: ["preparation"] });
    },
  });

  const current = book.data?.language ?? "";

  return (
    <div data-testid="book-language">
      <p className="block text-sm font-medium">{t.book.language}</p>
      <p className="mt-1 mb-2 text-xs text-muted">{t.book.languageNote}</p>
      {/* Searchable chips, not a <select>: every ISO 639-1 language is on the
          list (ADR-0056). A stored code the list does not name still shows,
          pressed, rather than being reset by opening this page. */}
      <LanguagePicker
        testID="book-language"
        languages={languages.data?.items ?? []}
        value={current}
        onChoose={(code) => {
          if (code !== current) choose.mutate(code);
        }}
        unsetLabel={t.book.languageUnset}
        disabled={choose.isPending || book.isPending}
      />
      {choose.isError && (
        <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
          {t.book.languageFailed}
        </p>
      )}
    </div>
  );
}
